import { useContext, useState, useRef, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'

import * as S from './styles'

import LoginError from './LoginError'

import ActiveTwoFactorModal from '../../../ActiveTwoFactorModal'
import TwoStepVerification from '../../../TwoStepVerification'

import ButtonPrimary from '../../../ui/Buttons/ButtonPrimary'
import AlertPopover from '../../../ui/AlertPopover'
import InputLogin from '../../../login/Input'

import api from '../../../../utils/api'
import { AuthContext } from '../../../../contexts/auth'
import { EmpresaContext } from '../../../../contexts/empresa'
import { RealtimeContext } from '../../../../contexts/realtime'
import { ThemeContext } from '../../../../contexts/theme'

function FormLogin(props) {
  const { twoFactorRequired } = useContext(ThemeContext)

  const [emailActive, setEmailActive] = useState(false)
  const [loginErrorActive, setLoginErrorActive] = useState(false)
  const [loginErrorMessage, setLoginErrorMessage] = useState({})
  const [passwordActive, setPasswordActive] = useState(false)
  const [popoverActive, setPopoverActive] = useState(false)
  const [disableButton, setDisableButton] = useState(true)
  const [userInfo, setUserInfo] = useState({})
  const [loginInfo, setLoginInfo] = useState({
    email: '',
    senha: ''
  })
  const [verificationModalActive, setVerificationModalActive] = useState(false)
  const [addTwoFactorModal, setAddTwoFactorModal] = useState(false)
  const [userToken, setUserToken] = useState(null)

  const emailRef = useRef(null)
  const passwordRef = useRef(null)

  const auth = useContext(AuthContext)
  const empresaContext = useContext(EmpresaContext)
  const { socket } = useContext(RealtimeContext)

  const history = useHistory()

  const validationFields = useCallback(() => {
    if (!loginInfo.email || !loginInfo.senha) {
      setDisableButton(true)
    } else {
      setDisableButton(false)
    }
  }, [loginInfo])

  useEffect(() => {
    validationFields()
  }, [loginInfo, validationFields])

  useEffect(() => {
    document.addEventListener('click', documentClick)
    return () => {
      document.removeEventListener('click', documentClick)
    }
  }, [])

  function documentClick(e) {
    if (emailRef.current && e.target !== emailRef.current && emailRef.current.value === '') {
      setEmailActive(false)
    }
    if (passwordRef.current && e.target !== passwordRef.current && passwordRef.current.value === '') {
      setPasswordActive(false)
    }
    setPopoverActive(false)
  }

  function handleChange(e) {
    setLoginInfo({
      ...loginInfo,
      [e.target.name]: e.target.value
    })
  }

  async function checkDocsSigned(token, companyToken) {
    const { data: docs } = await api.get('empresa-documentos', {
      headers: {
        authorization: 'Bearer ' + token,
        'token-empresa': companyToken
      }
    })

    if (docs.data.docs.length > 0 && !docs.data?.allDocsSigneds) {
      props.setSignContractModalActive(true)
      props.setContractDocs(docs.data.docs)

      props.setLoadingActive(false)

      return false
    }

    return true
  }

  async function finalizeLogin(token, user) {
    try {
      props.setLoadingActive(true)

      const { data: response } = await api.get('empresas/first', {
        headers: {
          authorization: 'Bearer ' + token
        }
      })

      const isDocsSigned = await checkDocsSigned(token, response.data.empresa.token)
      if (!isDocsSigned) return

      window.localStorage.setItem('wisepay-token', token)
      window.localStorage.setItem('wisepay-empresa', response.data.empresa.token)
      empresaContext.restoreEmpresaFromToken()

      auth.setUserInfo({
        isAuth: true,
        user: {
          id: user.id,
          email: user.email,
          nome: user.nome,
          root: user.root,
          rep: user.representante,
          admin: user.isAdmin,
          two_factor_enable: user.two_factor_enable,
          managerUser: user.gestor
        }
      })

      const { whitelabel_name } = JSON.parse(window.localStorage.getItem('whitelabel-theme'))
      socket.emit('user connected', whitelabel_name);

      if (response.data.empresa.id === 1 && user.root) {
        history.push('/dashboard')
      } else {
        history.push('/extrato/transacoes')
      }
    } catch (error) {
      props.setLoadingActive(false)
      setLoginErrorActive(true)
      setLoginErrorMessage({ description: 'Não foi possível fazer o login' })

      setLoginInfo({
        ...loginInfo,
        senha: ''
      })
    }
  }

  async function handleSubmit(e) {
    if (e) e.preventDefault()
    props.setLoadingActive(true)
    setLoginErrorMessage({})
    try {
      const { data: response } = await auth.login(loginInfo.email, loginInfo.senha)
      const { user, token } = response.data
      setUserInfo(user)
      setUserToken(token)

      if (twoFactorRequired && !user.two_factor_enable) {
        setAddTwoFactorModal(true)
        props.setLoadingActive(false)
        return
      }

      if (token) {
        await finalizeLogin(token, user)
      } else {
        setVerificationModalActive(true)
        props.setLoadingActive(false)
      }
    } catch (error) {
      props.setLoadingActive(false)

      const verifyError = error.response &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.type

      if (verifyError) {
        if (error.response.data.error.type === 'UnauthorizedError') {
          setPopoverActive(true)
        } else {
          const description = error.response.data.error.type === 'ForbiddenError'
            ? 'Usuário bloqueado'
            : 'Não foi possível fazer o login'
          setLoginErrorActive(true)
          setLoginErrorMessage({ description })
        }
      } else {
        setLoginErrorActive(true)
      }

      setLoginInfo({
        ...loginInfo,
        senha: ''
      })
    }
  }

  async function handleConfirmationCode(code) {
    props.setLoadingActive(true)

    try {
      const { data: response } = await api.post('/compare-code', {
        currentUser: userInfo,
        login: true,
        code,
      })

      if (response.data?.confirmAuthenticate) {
        confirmationVerificationCode(response.data?.token)
        return 'success'
      } else {
        return 'error'
      }
    } catch (error) {
      return 'error'
    } finally {
      props.setLoadingActive(false)
    }
  }

  async function confirmationVerificationCode(token) {
    await finalizeLogin(token, userInfo)
  }

  return (
    <S.MainContainer>
      <S.Card className={props.active === 'cardLogin' ? 'toFront' : ''} onClick={props.onHandleSetCardLogin}>
        <S.TextTop>
          <h2>Bem-vindo(a)</h2>
        </S.TextTop>

        <S.Form method='post'>
          <InputLogin
            dataCy='input-email'
            label='Email'
            name='email'
            id='email'
            type='text'
            status={emailActive}
            ref={emailRef}
            onChange={handleChange}
            onClick={() => setEmailActive(true)}
            value={loginInfo.email}
          />

          <S.PopoverContainer>
            <AlertPopover
              dataCy='popover-login-error'
              title='Esqueceu a sua senha?'
              content='Clique abaixo para recuperar'
              type='danger'
              active={popoverActive}
            />
          </S.PopoverContainer>

          <InputLogin
            dataCy='input-password'
            label='Senha'
            name='senha'
            id='password'
            type='password'
            borderDanger={popoverActive}
            status={passwordActive}
            ref={passwordRef}
            onChange={handleChange}
            onClick={() => setPasswordActive(true)}
            value={loginInfo.senha}
          >

            <S.ForgetPassword onClick={() => props.setRecoverModalActive(true)}>Esqueceu?</S.ForgetPassword>
          </InputLogin>

          <S.BtnContainer>
            <ButtonPrimary
              dataCy='button-submit'
              type='submit'
              disabled={disableButton}
              upperCase
              bold
              size='lg'
              text='Login'
              onClick={handleSubmit}
            />
          </S.BtnContainer>
        </S.Form>
      </S.Card>

      <LoginError
        active={loginErrorActive}
        onClick={() => setLoginErrorActive(false)}
        description={loginErrorMessage.description}
      />

      <TwoStepVerification
        active={verificationModalActive}
        setVerificationModalActive={setVerificationModalActive}
        handleConfirmationCode={handleConfirmationCode}
      />

      <ActiveTwoFactorModal
        active={addTwoFactorModal}
        token={userToken}
        setModalActive={setAddTwoFactorModal}
        onSuccess={handleSubmit}
      />
    </S.MainContainer>
  )
}

export default FormLogin
